import React, { useContext } from "react";
import { Controller, useForm } from "react-hook-form";
import { Button } from "iuvity-ui-components/dist/button";
import { Dialog } from "iuvity-ui-components/dist/dialog";
import { Dropdown } from "iuvity-ui-components/dist/dropdown";
import { CustomInput } from "../inputs/CustomInput";
import { DialogProps } from "../dialog/CustomDialog";
import { TRX_BENEFICIARY_OPTIONS, TRX_TYPE_OPTIONS } from "../../constants";
import { TransactionsFormSchema } from "../../validations/schemas";
import { AppContext } from "../../context";
import { ErrorMessage } from "@hookform/error-message";

interface TransactionCreatorProps {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  createFn: Function;
  reloadFn: Function;
  options: any;
  auxModalController: React.Dispatch<React.SetStateAction<DialogProps>>;
}

const trxDefaultValues = {
  name: "",
  systemfunction: "",
  labelkey: "",
  wildcardcode: "",
  authorizationflowname: "",
  type: "",
  beneficiaryregistrationtype: " ",
  isbatch: false,
  releaseversion: "",
};

export const TransactionCreator: React.FC<TransactionCreatorProps> = ({
  visible,
  setVisible,
  createFn,
  reloadFn,
  options,
  auxModalController,
}) => {
  const {
    t,
    appState: { user },
  } = useContext(AppContext);

  const {
    control,
    getValues,
    reset,
    formState: { errors, isValid, dirtyFields },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: trxDefaultValues,
  });

  const onCancel = () => {
    setVisible(false);
    reset(trxDefaultValues);
  };

  const onCreateTransaction = async () => {
    setVisible(false);
    const formValues = getValues();
    const actionStatus = await createFn("CREATE", {
      ...formValues,
      createdby: user?.name,
    });
    if (actionStatus === "SUCCESS") {
      reloadFn();
    }
    reset(trxDefaultValues);
  };

  const onConfirmCreate = () => {
    auxModalController((prev) => ({
      ...prev,
      visible: true,
      title: t("Transactions.createModalTitle"),
      content: t("Transactions.createModalContent"),
      onConfirm: onCreateTransaction,
    }));
  };

  const renderFooter = () => {
    return (
      <div className="flex align-items-center justify-content-flex-end">
        <Button
          label={t("Transactions.cancelBtnLabel")}
          onClick={onCancel}
          className="mr-4"
          variant="p-button-link"
        />
        <Button
          label={t("Transactions.createBtnLabel")}
          variant="p-button"
          disabled={!(isValid && dirtyFields.beneficiaryregistrationtype)}
          onClick={onConfirmCreate}
        />
      </div>
    );
  };

  return (
    <Dialog
      id="dialogScroll1"
      className="trx-m-f"
      header={t("Transactions.newTransactionTitle")}
      visible={visible}
      onHide={onCancel}
      style={{ width: "25rem" }}
      footer={renderFooter}
    >
      <div className="form">
        <Controller
          key="name"
          name="name"
          control={control}
          rules={TransactionsFormSchema(t).name}
          render={({ field }) => {
            return (
              <CustomInput
                id="input-name"
                className={`${errors["name"]?.message ? "p-invalid" : "mb-3"}`}
                label={t("Transactions.nameLabel")}
                placeholder={t("Transactions.namePlaceholderLabel")}
                type="text"
                autoComplete="off"
                style={{ width: "20rem" }}
                keyfilter={/^[^-+'"*;|?&=<>()%{}:@\u002F\u005B\u005C\u005D]*$/}
                maxLength={50}
                {...field}
              />
            );
          }}
        />
        <ErrorMessage
          errors={errors}
          name="name"
          //@ts-ignore
          render={({ message }) => (
            <small
              className="p-error block"
              style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
            >
              {message}
            </small>
          )}
        />

        <Controller
          key="labelkey"
          name="labelkey"
          control={control}
          rules={TransactionsFormSchema(t).labelkey}
          render={({ field }) => {
            return (
              <CustomInput
                id="input-labelkey"
                className={`${errors["labelkey"]?.message ? "p-invalid" : "mb-3"}`}
                label={t("Transactions.labelkeyLabel")}
                placeholder={t("Transactions.labelkeyPlaceholderLabel")}
                type="text"
                autoComplete="off"
                style={{ width: "20rem" }}
                keyfilter={/^[^-+'"*;|?&=<>()%{}:@\u002F\u005B\u005C\u005D]+$/}
                maxLength={60}
                {...field}
              />
            );
          }}
        />
        <ErrorMessage
          errors={errors}
          name="labelkey"
          //@ts-ignore
          render={({ message }) => (
            <small
              className="p-error block"
              style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
            >
              {message}
            </small>
          )}
        />

        <Controller
          key="type"
          name="type"
          control={control}
          render={({ field }) => {
            return (
              // @ts-ignore
              <Dropdown
                className="dropdown mb-3"
                label={t("Transactions.typeLabel")}
                placeholder={t("Transactions.typePlaceholder")}
                options={TRX_TYPE_OPTIONS(t)}
                filter={false}
                showClear={false}
                width="20rem"
                {...field}
              />
            );
          }}
        />
        <ErrorMessage
          errors={errors}
          name="type"
          //@ts-ignore
          render={({ message }) => (
            <small
              className="p-error block"
              style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
            >
              {message}
            </small>
          )}
        />

        <Controller
          key="systemfunction"
          name="systemfunction"
          control={control}
          rules={TransactionsFormSchema(t).systemfunction}
          render={({ field }) => {
            return (
              <CustomInput
                id="input-systemfunction"
                className={`${errors["systemfunction"]?.message ? "p-invalid" : "mb-3"}`}
                label={t("Transactions.systemfunctionLabel")}
                placeholder={t("Transactions.systemfunctionPlaceholderLabel")}
                type="text"
                autoComplete="off"
                style={{ width: "20rem" }}
                keyfilter={/^[^-+'"*;|?&=<>()%{}:@\u002F\u005B\u005C\u005D]+$/}
                maxLength={60}
                {...field}
              />
            );
          }}
        />
        <ErrorMessage
          errors={errors}
          name="systemfunction"
          //@ts-ignore
          render={({ message }) => (
            <small
              className="p-error block"
              style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
            >
              {message}
            </small>
          )}
        />

        <Controller
          key="wildcardcode"
          name="wildcardcode"
          control={control}
          rules={TransactionsFormSchema(t).wildcardcode}
          render={({ field }) => {
            return (
              <CustomInput
                id="input-wildcardcode"
                className={`${errors["wildcardcode"]?.message ? "p-invalid" : "mb-3"}`}
                label={t("Transactions.wildcardcodeLabel")}
                placeholder={t("Transactions.wildcardcodePlaceholderLabel")}
                type="text"
                autoComplete="off"
                style={{ width: "20rem" }}
                keyfilter={/^[0-9]*$/}
                maxLength={9}
                {...field}
              />
            );
          }}
        />
        <ErrorMessage
          errors={errors}
          name="wildcardcode"
          //@ts-ignore
          render={({ message }) => (
            <small
              className="p-error block"
              style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
            >
              {message}
            </small>
          )}
        />

        <Controller
          key="authorizationflowname"
          name="authorizationflowname"
          control={control}
          rules={TransactionsFormSchema(t).authorizationflowname}
          render={({ field }) => {
            return (
              <CustomInput
                id="input-authorizationflowname"
                className={`${
                  errors["authorizationflowname"]?.message ? "p-invalid" : "mb-3"
                }`}
                label={t("Transactions.authorizationflownameLabel")}
                placeholder={t(
                  "Transactions.authorizationflownamePlaceholderLabel"
                )}
                type="text"
                autoComplete="off"
                style={{ width: "20rem" }}
                keyfilter={/^[^-+'"*;|?&=<>()%{}:@\u002F\u005B\u005C\u005D]+$/}
                maxLength={60}
                {...field}
              />
            );
          }}
        />
        <ErrorMessage
          errors={errors}
          name="authorizationflowname"
          //@ts-ignore
          render={({ message }) => (
            <small
              className="p-error block"
              style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
            >
              {message}
            </small>
          )}
        />

        <Controller
          key="beneficiaryregistrationtype"
          name="beneficiaryregistrationtype"
          control={control}
          render={({ field }) => {
            return (
              // @ts-ignore
              <Dropdown
                className="dropdown mb-3"
                label={t("Transactions.beneficiaryregistrationtypeLabel")}
                placeholder={t(
                  "Transactions.beneficiaryregistrationtypePlaceholder"
                )}
                options={TRX_BENEFICIARY_OPTIONS(t)}
                filter={false}
                showClear={false}
                width="20rem"
                {...field}
              />
            );
          }}
        />
        <ErrorMessage
          errors={errors}
          name="beneficiaryregistrationtype"
          //@ts-ignore
          render={({ message }) => (
            <small
              className="p-error block"
              style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
            >
              {message}
            </small>
          )}
        />

        <Controller
          key="isbatch"
          name="isbatch"
          control={control}
          rules={TransactionsFormSchema(t).isbatch}
          render={({ field }) => {
            return (
              <CustomInput
                id="input-isbatch"
                className={`${errors["isbatch"]?.message ? "p-invalid" : "mb-3"}`}
                label={t("Transactions.isbatchLabel")}
                placeholder={t("Transactions.isbatchPlaceholderLabel")}
                type="boolean"
                autoComplete="off"
                style={{ width: "20rem" }}
                {...field}
              />
            );
          }}
        />
        <ErrorMessage
          errors={errors}
          name="isbatch"
          //@ts-ignore
          render={({ message }) => (
            <small
              className="p-error block"
              style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
            >
              {message}
            </small>
          )}
        />

        <Controller
          key="releaseversion"
          name="releaseversion"
          control={control}
          rules={TransactionsFormSchema(t).releaseversion}
          render={({ field }) => {
            return (
              // @ts-ignore
              <Dropdown
                className="dropdown mb-3"
                label={t(`Transactions.releaseversionLabel`)}
                placeholder={t(`Transactions.releaseversionPlaceholder`)}
                options={options?.versions.sort((a: any, b: any) =>
                  b.label.localeCompare(a.label)
                )}
                filter={false}
                showClear={false}
                width="20rem"
                {...field}
              />
            );
          }}
        />
        <ErrorMessage
          errors={errors}
          name="releaseversion"
          //@ts-ignore
          render={({ message }) => (
            <small
              className="p-error block"
              style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
            >
              {message}
            </small>
          )}
        />
      </div>
    </Dialog>
  );
};
